// src/components/ChatWithSecurity.js - Enhanced Chat with security level support
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Settings, Shield, Bot, User, MessageCircleHeart, Brain } from 'lucide-react';
import axios from 'axios';
import Button from './Button';
import Icon from './Icon';
import SecurityIndicator from './SecurityIndicator';
import PaywallModal from './PaywallModal';
import TherapistActionModal from './TherapistActionModal';
import AIReviewPanel from './AIReviewPanel';
import apiService, { SECURITY_LEVELS } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ChatWithSecurity = React.memo(({ token, patientId = null, isTherapistView = false, selectedPatient = null, onCreditUsed = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [isLoadingChat, setIsLoadingChat] = useState(false); // Guard for preventing duplicate loads
  const [showHistory, setShowHistory] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [therapist, setTherapist] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [credits, setCredits] = useState(null);
  const [showTherapistModal, setShowTherapistModal] = useState(false);
  const [showAIReviewPanel, setShowAIReviewPanel] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [lifeThemeContext, setLifeThemeContext] = useState(null);
  const [isGeneratingWelcome, setIsGeneratingWelcome] = useState(false);
  const [lifeThemeContextSent, setLifeThemeContextSent] = useState(false); // Track if context already sent
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false); // Track if history has been loaded
  const [symptomContext, setSymptomContext] = useState(null);
  const [symptomContextSent, setSymptomContextSent] = useState(false); // Track if symptom context already sent
  const [isNewConversation, setIsNewConversation] = useState(false); // Trigger decision engine reset on next message
  const [showDecisionDebug, setShowDecisionDebug] = useState(false); // Toggle debug panel
  const [lastDecisionDebug, setLastDecisionDebug] = useState(null); // Latest engine state
  const [pendingInsight, setPendingInsight] = useState(null); // Insight awaiting user confirmation
  const [pendingFeelingCheck, setPendingFeelingCheck] = useState(false); // 1-10 rating widget
  const messagesEndRef = useRef(null);

  // Detect life theme context from navigation state
  useEffect(() => {
    if (location.state?.lifeThemeContext) {
      console.log('🎯 Life Theme Context detected:', location.state.lifeThemeContext);
      setLifeThemeContext(location.state.lifeThemeContext);
    }
    if (location.state?.symptomContext) {
      console.log('🩺 Symptom Context detected:', location.state.symptomContext);
      setSymptomContext(location.state.symptomContext);
    }
  }, [location.state]);

  // Generate welcome message AFTER chat history is loaded
  useEffect(() => {
    const generateWelcomeMessage = async () => {
      // Only generate if:
      // 1. Chat history has been loaded
      // 2. Life theme context exists
      // 3. No messages exist OR no life theme messages exist
      // 4. Not already generating
      if (!chatHistoryLoaded || !lifeThemeContext || isGeneratingWelcome || lifeThemeContextSent) {
        return;
      }

      // Check if there are already life theme messages for this theme
      const hasLifeThemeMessages = messages.some(msg => 
        msg.messageType === 'life_theme_start' || 
        msg.messageType === 'life_theme_welcome'
      );

      if (hasLifeThemeMessages) {
        console.log('✅ Life theme messages already exist, skipping welcome generation');
        setLifeThemeContextSent(true);
        return;
      }

      if (messages.length > 0) {
        console.log('ℹ️ Chat history exists, but no life theme messages. Generating welcome...');
      }
      
      setIsGeneratingWelcome(true);
      setLoading(true);
      
      try {
        const context = lifeThemeContext;
        const welcomePrompt = `Hej! Jag vill utforska livstemat "${context.themeName}".`;
        
        console.log('🎯 Generating welcome message for life theme exploration...');
        
        const response = await apiService.sendChatMessage(
          welcomePrompt,
          token,
          [], // No preloaded context
          context // Life theme context
        );
        
        // Add user's implicit message
        const userMessage = {
          role: 'user',
          content: `[Började utforskning av livstemat: ${context.themeName}]`,
          timestamp: new Date().toISOString(),
          id: Date.now() + '_user_start',
          securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
          messageType: 'life_theme_start'
        };
        
        // Add AI's welcome response
        const aiMessage = {
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          id: Date.now() + '_ai_welcome',
          securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
          messageType: 'life_theme_welcome'
        };
        
        setMessages(prev => [...prev, userMessage, aiMessage]);
        console.log('✅ Welcome message generated successfully');
        
        // Mark that context has been sent
        setLifeThemeContextSent(true);
        
      } catch (error) {
        console.error('❌ Error generating welcome message:', error);
        // Fallback to static greeting if API fails
        const fallbackGreeting = {
          role: 'assistant',
          content: `Välkommen till en djupare utforskning av livstemat "${lifeThemeContext.themeName}". 

Vi kommer fokusera på tre viktiga områden:
🌱 **Tidiga minnen** - Var och när började detta mönster?
🔥 **Triggers** - Vad aktiverar livstemat idag?
🛡️ **Copingstrategier** - Hur hanterar du dessa situationer?

Vart vill du börja? Eller berätta fritt vad som känns mest relevant för dig just nu.`,
          timestamp: new Date().toISOString(),
          id: Date.now() + '_greeting',
          securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
          messageType: 'system_intro'
        };
        setLifeThemeContextSent(true); // Prevent retry loop on error
        setMessages(prev => [...prev, fallbackGreeting]);
      } finally {
        setLoading(false);
        setIsGeneratingWelcome(false);
      }
    };
    
    generateWelcomeMessage();
  }, [chatHistoryLoaded, lifeThemeContext, lifeThemeContextSent]);

  // Generate welcome message for symptom context AFTER chat history is loaded
  useEffect(() => {
    const generateSymptomWelcome = async () => {
      // Only generate if:
      // 1. Chat history has been loaded
      // 2. Symptom context exists
      // 3. No life theme context (that takes priority)
      // 4. Not already generating
      if (!chatHistoryLoaded || !symptomContext || lifeThemeContext || isGeneratingWelcome || symptomContextSent) return;

      const hasSymptomMessages = messages.some(
        (msg) => msg.messageType === 'symptom_start' || msg.messageType === 'symptom_welcome'
      );
      if (hasSymptomMessages) {
        console.log('✅ Symptom messages already exist, skipping welcome generation');
        setSymptomContextSent(true);
        return;
      }

      setIsGeneratingWelcome(true);
      setLoading(true);

      try {
        const primaryLabel =
          symptomContext.selectedPrimary?.name ||
          symptomContext.selectedPrimary?.label ||
          symptomContext.symptomPath?.[0] ||
          'detta';

        // Build full path label including secondary/tertiary for display and prompt
        const pathLabels = [
          symptomContext.selectedPrimary?.name || symptomContext.selectedPrimary?.label,
          symptomContext.selectedSecondary?.name || symptomContext.selectedSecondary?.label,
          symptomContext.selectedTertiary?.name || symptomContext.selectedTertiary?.label,
        ].filter(Boolean);
        const fullPathLabel = pathLabels.join(' → ');

        const welcomePrompt = pathLabels.length > 1
          ? `Hej! Jag vill prata om ${primaryLabel}, specifikt kopplat till ${pathLabels.slice(1).join(' och ')}.`
          : `Hej! Jag vill prata om ${primaryLabel}.`;

        console.log('🩺 Generating welcome message for symptom context...');

        const response = await apiService.sendChatMessage(
          welcomePrompt,
          token,
          [],          // No preloaded context
          null,        // No life theme context
          symptomContext
        );

        const severityLabel = symptomContext.severityScore != null
          ? ` · Allvarlighetsgrad: ${symptomContext.severityScore}/10`
          : '';

        const userMessage = {
          role: 'user',
          content: `[Startade session för: ${fullPathLabel}${severityLabel}]`,
          timestamp: new Date().toISOString(),
          id: Date.now() + '_user_symptom_start',
          securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
          messageType: 'symptom_start',
        };

        const aiMessage = {
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          id: Date.now() + '_ai_symptom_welcome',
          securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
          messageType: 'symptom_welcome',
        };

        setMessages((prev) => [...prev, userMessage, aiMessage]);
        console.log('✅ Symptom welcome message generated successfully');
        setSymptomContextSent(true);
      } catch (error) {
        console.error('❌ Error generating symptom welcome:', error);
        const pathLabels = [
          symptomContext.selectedPrimary?.name || symptomContext.selectedPrimary?.label,
          symptomContext.selectedSecondary?.name || symptomContext.selectedSecondary?.label,
          symptomContext.selectedTertiary?.name || symptomContext.selectedTertiary?.label,
        ].filter(Boolean);
        const fallbackGreeting = {
          role: 'assistant',
          content: `Välkommen! Jag är här för att prata om ${pathLabels.join(', ') || 'det du vill dela'}. Berätta lite om vad som är på gång.`,
          timestamp: new Date().toISOString(),
          id: Date.now() + '_symptom_fallback',
          securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
          messageType: 'system_intro',
        };
        setSymptomContextSent(true); // Prevent retry loop on error
        setMessages((prev) => [...prev, fallbackGreeting]);
      } finally {
        setLoading(false);
        setIsGeneratingWelcome(false);
      }
    };

    generateSymptomWelcome();
  }, [chatHistoryLoaded, symptomContext, lifeThemeContext, symptomContextSent]);

  // Load chat history and stats when component mounts
  useEffect(() => {
    if (token && (patientId || !isTherapistView) && !isLoadingChat) {
      // Reset messages when parameters change to prevent accumulation
      setMessages([]);
      setChatHistoryLoaded(false); // Reset loaded state
      loadChatHistory();
      loadChatStats();
      // Load therapist info and credits for patient view (not therapist view)
      if (!isTherapistView) {
        loadTherapistInfo();
        loadCredits();
      }
    }
    
    // Cleanup function to reset state when component unmounts or parameters change
    return () => {
      console.log(`🧹 CLEANUP: Resetting messages (was ${messages.length} messages)`);
      // Temporarily disabled to debug message display issue
      // setMessages([]);
      // setError(null);
    };
  }, [token, patientId, isTherapistView, lifeThemeContext, symptomContext]);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    if (!token || isLoadingChat) return;
  
    setIsLoadingChat(true);
    setLoadingHistory(true);
    setError(null);
    
    try {
      console.log(`🔐 Loading chat history with ultra-secure encryption...`);
      if (lifeThemeContext) {
        console.log(`🌱 Filtering for life theme: ${lifeThemeContext.themeName} (ID: ${lifeThemeContext.themeId})`);
      }
      
      const data = await apiService.getChatHistory(token, {
        limit: 50,
        patientId: isTherapistView ? patientId : null,
        lifeThemeId: lifeThemeContext?.themeId || null // Filter by life theme if context exists
      });

      console.log(`✅ Chat history loaded with ${data.security_level} encryption`);

      // Convert API data to message format
      const historyMessages = data.conversations
        .sort((a, b) => new Date(a.timestamp + 'Z') - new Date(b.timestamp + 'Z')) // Sort by timestamp oldest first (UTC)
        .flatMap((conv, index) => [
          {
            role: 'user',
            content: conv.message,
            timestamp: conv.timestamp, // Keep raw timestamp string for proper UTC handling
            id: conv.id + '_user',
            conversationId: conv.id,
            importance: conv.importance_score,
            messageType: conv.message_type,
            isArchived: conv.is_archived,
            securityLevel: data.security_level
          },
          {
            role: 'assistant',
            content: conv.response,
            timestamp: new Date(new Date(conv.timestamp + 'Z').getTime() + 1000).toISOString().slice(0, -1), // Add 1 second, keep as UTC string
            id: conv.id + '_assistant',
            conversationId: conv.id,
            importance: conv.importance_score,
            messageType: conv.message_type,
            isArchived: conv.is_archived,
            securityLevel: data.security_level
          }
        ])
        .sort((a, b) => new Date(a.timestamp + 'Z') - new Date(b.timestamp + 'Z')); // Final sort by timestamp to ensure proper order (UTC)

      setMessages(historyMessages);
      console.log(`✅ Loaded ${historyMessages.length / 2} conversations with ${data.security_level} security`);
      console.log(`🔍 Messages state now has ${historyMessages.length} total messages`);
      setChatHistoryLoaded(true); // Mark that history has been loaded
      
    } catch (error) {
      console.error('❌ Error loading chat history:', error);
      setError('Kunde inte ladda chatthistorik: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingHistory(false);
      setIsLoadingChat(false); // Reset the guard
    }
  };

  const loadChatStats = async () => {
    if (!token) return;

    try {
      const statsApiStart = performance.now();
      console.log(`📊 STATS API: Starting chat stats request at ${new Date().toLocaleTimeString()}`);
      
      const stats = await apiService.getChatStats(token, isTherapistView ? patientId : null);
      
      const statsApiEnd = performance.now();
      const statsApiTime = statsApiEnd - statsApiStart;
      console.log(`📊 STATS API: Completed in ${statsApiTime.toFixed(2)}ms (${(statsApiTime/1000).toFixed(2)}s) at ${new Date().toLocaleTimeString()}`);
      
      setStats(stats);
    } catch (error) {
      console.error('❌ Error loading stats:', error);
      // Don't show error for stats, just log it
    }
  };

  const loadTherapistInfo = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE}/api/invitations/my-therapist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.has_therapist) {
        setTherapist(response.data.therapist);
      }
    } catch (error) {
      console.error('❌ Error loading therapist info:', error);
      // Don't show error for therapist info, just log it
    }
  };

  const loadCredits = async () => {
    if (!token || isTherapistView) return; // Don't load credits for therapist view

    try {
      const creditResponse = await fetch(`${API_BASE}/api/v1/credits/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (creditResponse.ok) {
        const creditData = await creditResponse.json();
        setCredits(creditData.current_balance);
      } else {
        console.warn('Could not load credit balance');
        setCredits(0); // Default to 0 if we can't load credits
      }
    } catch (error) {
      console.error('❌ Error loading credits:', error);
      setCredits(0); // Default to 0 on error
    }
  };

  const sendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || loading) return;

    // ⏱️ FRONTEND TIMING: Start timing
    const frontendStartTime = performance.now();
    console.log('⏱️ FRONTEND: Starting message send at', new Date().toLocaleTimeString());

    // Check credits before sending (only for patients, not therapists)
    if (!isTherapistView) {
      try {
        const creditResponse = await fetch(`${API_BASE}/api/v1/credits/balance`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (creditResponse.ok) {
          const creditData = await creditResponse.json();
          setCredits(creditData.current_balance);
          if (creditData.current_balance <= 0) {
            setShowPaywall(true);
            setError("Du har inga credits kvar. Välj en plan för att fortsätta chatta.");
            return;
          }
        } else if (creditResponse.status === 401) {
          // Token expired - will be handled by axios interceptor
          console.warn('🕒 JWT Token expired during credit check');
          return;
        } else {
          console.warn('Could not check credit balance, proceeding with message');
        }
      } catch (err) {
        if (err.message.includes('401')) {
          console.warn('🕒 JWT Token expired during credit check');
          return;
        }
        console.warn('Credit check failed, proceeding with message:', err);
      }
    }

    const now = new Date();
    const userMessage = {
      role: 'user', 
      content: trimmedInput, 
      timestamp: now.toISOString().slice(0, -1), // Convert to UTC string format like server
      id: now.getTime() + '_user_temp',
      securityLevel: SECURITY_LEVELS.ULTRA_SECURE
    };
    
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = trimmedInput;
    setInput('');
    setLoading(true);

    // ⏱️ FRONTEND TIMING: Pre-API timing
    const preApiTime = performance.now();
    const prepTime = preApiTime - frontendStartTime;
    console.log(`⏱️ FRONTEND: Message preparation took ${prepTime.toFixed(2)}ms`);

    try {
      console.log(`🔐 Sending message via ultra-secure encryption`);
      
      // 🎯 Life Theme Context logging
      if (lifeThemeContext) {
        console.log(`🎯 Including Life Theme Context: ${lifeThemeContext.themeName || ''}`);
      }
      
      // 🚀 OPTIMIZATION: Prepare context from already-loaded messages
      const recentMessages = messages
        .filter(msg => !msg.isArchived && msg.role) // Only non-archived messages with role
        .slice(-10) // Last 5 conversations (10 messages)
        .map(msg => ({
          role: msg.role,
          content: msg.content,
          importance: msg.importance || 1,
          timestamp: msg.timestamp
        }));
      
      console.log(`🚀 CONTEXT OPTIMIZATION: Sending ${recentMessages.length} pre-loaded messages to backend`);
      
      // ⏱️ FRONTEND TIMING: API call timing
      const apiStartTime = performance.now();
      
      // Only send life theme context on first message (welcome message already sent it)
      const contextToSend = (lifeThemeContext && !lifeThemeContextSent) ? lifeThemeContext : null;
      if (contextToSend) {
        console.log(`🎯 Including Life Theme Context (first message): ${contextToSend.themeName || ''}`);
        setLifeThemeContextSent(true);
      }

      // Only send symptom context on first message
      const symptomContextToSend = (symptomContext && !symptomContextSent) ? symptomContext : null;
      if (symptomContextToSend) {
        console.log(`🩺 Including Symptom Context (first message): ${symptomContextToSend.symptomPath?.join(' → ') || ''}`);
        setSymptomContextSent(true);
      }

      const sendAsNewConversation = isNewConversation;
      if (isNewConversation) setIsNewConversation(false); // Reset after first send

      const response = await apiService.sendChatMessage(
        messageToSend,
        token,
        recentMessages,
        contextToSend,           // ⭐ life theme – only first message
        symptomContextToSend,    // ⭐ symptom – only first message
        sendAsNewConversation    // ⭐ signals decision engine to reset to OPENING
      );
      const apiEndTime = performance.now();
      const apiTime = apiEndTime - apiStartTime;
      
      console.log(`⏱️ FRONTEND: API call took ${apiTime.toFixed(2)}ms (${(apiTime/1000).toFixed(2)}s)`);

      // Use credit after successful message (only for patients)
      if (!isTherapistView) {
        try {
          await fetch(`${API_BASE}/api/v1/credits/use`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('✅ Credit used successfully');
          // Update credit display immediately
          if (onCreditUsed) {
            onCreditUsed();
          }
        } catch (err) {
          console.error('Failed to use credit:', err);
          // Continue anyway - message was already sent
        }
      }

      console.log(`✅ AI response received with ${response.security_level} security`);

      // Store decision engine debug if present
      if (response.metadata?.decision_engine) {
        setLastDecisionDebug(response.metadata.decision_engine);
        // Show 1-10 widget when engine triggers check_in_feeling
        // Show 1-10 widget when engine triggers check_in_feeling
        if (response.metadata.decision_engine.decision_tool_used === 'check_in_feeling') {
          setPendingFeelingCheck(true);
        }
      }

      // Offer to save insight if Claude flagged one
      if (response.metadata?.suggested_insight?.text) {
        setPendingInsight(response.metadata.suggested_insight);
      }

      const aiMessage = { 
        role: 'assistant', 
        content: response.response, 
        timestamp: new Date().toISOString().slice(0, -1), // Convert to UTC string format like server
        id: Date.now() + '_assistant_temp',
        // Add smart metadata
        importance: response.metadata?.importance_score,
        messageType: response.metadata?.message_type,
        conversationId: response.metadata?.conversation_id,
        securityLevel: response.security_level
      };
      
      setMessages(prev => {
        const newMessages = [...prev, aiMessage];
        // Sort to ensure proper chronological order
        return newMessages.sort((a, b) => a.timestamp - b.timestamp);
      });
      
      // ⏱️ FRONTEND TIMING: Complete timing summary
      const frontendEndTime = performance.now();
      const totalFrontendTime = frontendEndTime - frontendStartTime;
      const postApiTime = frontendEndTime - apiEndTime;
      
      console.log('⏱️ FRONTEND TIMING SUMMARY:');
      console.log(`   🚀 Message preparation: ${prepTime.toFixed(2)}ms`);
      console.log(`   🌐 API call (backend): ${apiTime.toFixed(2)}ms (${(apiTime/1000).toFixed(2)}s)`);
      console.log(`   🎨 UI update: ${postApiTime.toFixed(2)}ms`);
      console.log(`   📊 TOTAL FRONTEND: ${totalFrontendTime.toFixed(2)}ms (${(totalFrontendTime/1000).toFixed(2)}s)`);
      console.log(`   ⏰ Completed at: ${new Date().toLocaleTimeString()}`);
      
      // 🌱 PROGRESSIVE ANAMNESIS - Already integrated in backend!
      // The ultra-secure-chat endpoint already:
      // 1. Analyzes the message with 20 previous messages + existing profile
      // 2. Updates the progressive anamnesis profile automatically
      // 3. Returns comprehensive response with all metadata
      console.log('🌱 Progressive anamnesis analysis completed by backend automatically');
      
      // Check for notifications based on metadata
      if (response.metadata && !isTherapistView) {
        addNotification(response.metadata, response.response);
      }
      
      // Reload stats after new message
      console.log(`📊 Loading chat stats from ${API_BASE}/api/ultra-secure-chat/ultra-secure-chat/stats`);
      const statsStartTime = performance.now();
      loadChatStats();
      const statsEndTime = performance.now();
      console.log(`⏱️ FRONTEND: loadChatStats took ${(statsEndTime - statsStartTime).toFixed(2)}ms`);
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      
      // ⏱️ FRONTEND TIMING: Error timing
      const errorTime = performance.now();
      const totalErrorTime = errorTime - frontendStartTime;
      console.log(`⏱️ FRONTEND: Error occurred after ${totalErrorTime.toFixed(2)}ms (${(totalErrorTime/1000).toFixed(2)}s)`);
      
      const errorMessage = { 
        role: 'error', 
        content: 'Kunde inte skicka meddelande: ' + (error.response?.data?.detail || error.message), 
        timestamp: new Date().toISOString().slice(0, -1), // Convert to UTC string format like server
        id: Date.now() + '_error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const sendFeelingScore = async (score) => {
    setPendingFeelingCheck(false);
    const now = new Date();
    const userMessage = {
      role: 'user',
      content: `Mitt mående just nu: ${score}/10`,
      timestamp: now.toISOString().slice(0, -1),
      id: now.getTime() + '_feeling_user',
      securityLevel: SECURITY_LEVELS.ULTRA_SECURE,
      messageType: 'feeling_check',
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const recentMessages = messages
        .filter(msg => !msg.isArchived && msg.role)
        .slice(-10)
        .map(msg => ({ role: msg.role, content: msg.content, importance: msg.importance || 1, timestamp: msg.timestamp }));

      const response = await apiService.sendChatMessage(
        `Mitt mående just nu: ${score}/10 (skala: 1=mår bra, 10=akut kris)`,
        token,
        recentMessages,
        null,
        null,
        false,
        score
      );

      if (response.metadata?.decision_engine) {
        setLastDecisionDebug(response.metadata.decision_engine);
      }
      if (response.metadata?.suggested_insight?.text) {
        setPendingInsight(response.metadata.suggested_insight);
      }

      const aiMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString().slice(0, -1),
        id: Date.now() + '_assistant_feeling',
        importance: response.metadata?.importance_score,
        messageType: response.metadata?.message_type,
        securityLevel: response.security_level,
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('❌ Error sending feeling score:', err);
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (metadata, response) => {
    if (metadata.importance_score >= 4) {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'high-importance',
        message: 'Viktigt meddelande identifierat',
        timestamp: new Date().toISOString().slice(0, -1) // Convert to UTC string format like server
      }]);
    }
  };

  const startNewConversation = () => {
    // Insert a visual divider – keeps full history scrollable
    const divider = {
      role: 'divider',
      id: Date.now() + '_divider',
      timestamp: new Date().toISOString().slice(0, -1),
    };
    setMessages(prev => [...prev, divider]);
    setIsNewConversation(true); // Next message will carry new_conversation: true
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openSecuritySettings = () => {
    // Navigate to security settings or open modal
    navigate('/security-settings');
  };

  const handleOpenAIReview = (conversationId, messageId = null) => {
    // Clean messageId by removing _assistant suffix for API calls
    const cleanMessageId = messageId ? messageId.replace('_assistant', '') : null;
    
    console.log(`🤖 Opening AI Review for conversation ${conversationId}`, messageId ? `message ${messageId} (cleaned: ${cleanMessageId})` : '');
    setSelectedConversationId(conversationId);
    setShowAIReviewPanel(true);
    if (cleanMessageId) {
      setHighlightedMessageId(cleanMessageId);
    }
  };

  const closeAIReview = () => {
    setShowAIReviewPanel(false);
    setSelectedConversationId(null);
    setHighlightedMessageId(null);
  };

  const handleHighlightMessage = (messageId) => {
    setHighlightedMessageId(messageId);
    // Scroll to message if needed
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const getTherapistName = () => {
    // If therapist is viewing patient's chat, show "AI-stöd"
    if (isTherapistView) {
      return 'Terapeut';
    }
    // For patient view, show their therapist's first name if available
    if (therapist?.first_name) {
      return therapist.first_name;
    }
    return 'Terapeut';
  };

  const getImportanceBadge = (importance) => {
    if (!importance || importance < 4) return null;
    
    return (
      <span className={`inline-block py-0.5 px-1.5 rounded-2xl text-xs font-medium ml-2 ${
        importance === 5 
          ? 'bg-red-100 text-red-800' 
          : 'bg-yellow-100 text-yellow-800'
      }`}>
        {importance}⭐
      </span>
    );
  };

  const getTypeBadge = (messageType, role) => {
    if (!messageType || messageType === 'regular') return null;
    
    // Only show positive/constructive message types to users for clinical safety
    const typeConfig = {
      // 'crisis': Hidden from users - handled privately by therapists for clinical safety
      'breakthrough': { 
        emoji: '🎉', 
        label: 'Genombrott', 
        classes: role === 'user' 
          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm border border-white' 
          : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm'
      },
      'reflection': { 
        emoji: '🤔', 
        label: 'Reflektion', 
        classes: role === 'user'
          ? 'bg-white/20 text-white border border-white'
          : 'bg-blue-50 text-blue-700 border border-blue-200'
      },
      'therapist_suggestion': {
        emoji: '👨‍⚕️', 
        label: 'Terapeutförslag', 
        classes: role === 'user'
          ? 'bg-white/20 text-white border border-white'
          : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-sm'
      }
    };
    
    const config = typeConfig[messageType];
    if (!config) return null;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${config.classes}`}>
        {config.emoji} {config.label}
      </span>
    );
  };

  const formatTime = (timestamp) => {
    let date;
    
    // Handle different timestamp formats
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      // If the timestamp string doesn't have timezone info, assume it's UTC and add 'Z'
      if (timestamp.includes('T') && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        date = new Date(timestamp + 'Z'); // Add Z to indicate UTC
      } else {
        date = new Date(timestamp);
      }
    } else {
      date = new Date(timestamp);
    }
    
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    // For messages from today (less than 24 hours), show time
    if (days === 0) {
      // For very recent messages (less than 1 hour), show time format
      if (hours === 0) {
        return date.toLocaleTimeString('sv-SE', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else {
        return date.toLocaleTimeString('sv-SE', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    } else if (days === 1) {
      return 'Igår ' + date.toLocaleTimeString('sv-SE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return `${days} ${days === 1 ? 'dag' : 'dagar'} sedan`;
    }
  };

  // Add navigation breadcrumb for therapist view
  const renderBreadcrumb = () => {
    if (!isTherapistView || !selectedPatient) return null;

    return (
      <div className="mb-4">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <button
                onClick={() => navigate('/therapist')}
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                <Icon name="users" size={16} className="mr-2" />
                Klienter
              </button>
            </li>
            <li>
              <div className="flex items-center">
                <Icon name="chevron-right" size={16} className="text-gray-400" />
                <button
                  onClick={() => navigate(`/therapist/client/${selectedPatient.id}`)}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {selectedPatient.name}
                </button>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <Icon name="chevron-right" size={16} className="text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Chat</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screenfont-sans">
      {/* Add breadcrumb for therapist view */}
      {isTherapistView && renderBreadcrumb()}
      
      {/* Modern Chat Header - sticky */}
      <div className="sticky  top-12 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
              <Icon name="bot" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isTherapistView && selectedPatient 
                  ? `${selectedPatient.name}` 
                  : 'AI-stöd'}
              </h2>
              <p className="text-sm text-emerald-600 font-medium">
                <Shield className="w-3 h-3 inline mr-1" />
                Säker kryptering aktiv
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isTherapistView && symptomContext && (
              <button
                onClick={() => navigate('/client/symptom-selector')}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-xs text-gray-600"
                title="Ändra fokusområde"
              >
                <span>{symptomContext.selectedPrimary?.emoji || '🩺'}</span>
                <span className="hidden sm:inline font-medium">
                  {[
                    symptomContext.selectedPrimary?.name || symptomContext.selectedPrimary?.label,
                    symptomContext.selectedSecondary?.name || symptomContext.selectedSecondary?.label,
                    symptomContext.selectedTertiary?.name || symptomContext.selectedTertiary?.label,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <Icon name="pencil" size={12} className="text-gray-400" />
              </button>
            )}

            
          </div>
        </div>

        {/* Decision Engine Debug Panel */}
        {showDecisionDebug && (
          <div className="mx-4 mt-0 mb-2 p-3 bg-purple-50 border border-purple-200 rounded-xl text-xs font-mono space-y-2">
            {!lastDecisionDebug ? (
              <div className="text-purple-400 italic text-center py-1">
                🧠 Skicka ett meddelande för att se beslutsstödsdata
              </div>
            ) : (<>

            {/* Row 1: State + turn counter */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-purple-500 uppercase tracking-wide text-[10px]">State</span>
                <span className="px-2 py-0.5 bg-purple-200 text-purple-900 rounded-full font-bold">
                  {lastDecisionDebug.state}
                </span>
                <span className={`text-[10px] font-semibold ${
                  (lastDecisionDebug.turns_in_state ?? 0) >= 4
                    ? 'text-red-500'
                    : 'text-purple-400'
                }`}>
                  {lastDecisionDebug.turns_in_state ?? '?'} turns i state
                  {(lastDecisionDebug.turns_in_state ?? 0) >= 4 ? ' ⚠️' : ''}
                </span>
                {lastDecisionDebug.state_recommended && lastDecisionDebug.state_recommended !== lastDecisionDebug.state && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                    ✦ föreslår → {lastDecisionDebug.state_recommended}
                  </span>
                )}
              </div>
              <span className="text-purple-400">Turn {lastDecisionDebug.turn}</span>
            </div>

            {/* Row 2: Claude's decision */}
            <div className="border-t border-purple-200 pt-2">
              <span className="text-purple-500 uppercase tracking-wide text-[10px] block mb-1">Claude beslutade</span>
              <div className="flex flex-wrap gap-1.5">
                {lastDecisionDebug.decision_state_after ? (
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${
                    lastDecisionDebug.decision_state_after !== lastDecisionDebug.state
                      ? 'bg-blue-200 text-blue-900'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    state_after: {lastDecisionDebug.decision_state_after}
                    {lastDecisionDebug.decision_state_after !== lastDecisionDebug.state ? ' ⬆' : ' (ingen ändring)'}
                  </span>
                ) : (
                  <span className="text-gray-400 italic">inget decision-block i svaret</span>
                )}
                {lastDecisionDebug.decision_tool_used && (
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full">
                    🔧 använde: {lastDecisionDebug.decision_tool_used}
                  </span>
                )}
                {lastDecisionDebug.decision_deviated && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full" title={lastDecisionDebug.decision_notes || ''}>
                    ⚠️ avvek{lastDecisionDebug.decision_notes ? `: ${lastDecisionDebug.decision_notes}` : ''}
                  </span>
                )}
                {lastDecisionDebug.rule_followed && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    📌 {lastDecisionDebug.rule_followed}
                  </span>
                )}
              </div>
            </div>

            {/* Row 3: Signals */}
            <div className="border-t border-purple-200 pt-2">
              <span className="text-purple-500 uppercase tracking-wide text-[10px] block mb-1">Signaler</span>
              <div className="flex flex-wrap gap-1.5">
                {lastDecisionDebug.affect && (
                  <span className={`px-2 py-0.5 rounded-full ${
                    lastDecisionDebug.affect === 'high' ? 'bg-orange-200 text-orange-900' :
                    lastDecisionDebug.affect === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    affekt: {lastDecisionDebug.affect}{lastDecisionDebug.affect_type ? ` · ${lastDecisionDebug.affect_type}` : ''}
                  </span>
                )}
                {lastDecisionDebug.crisis && (
                  <span className="px-2 py-0.5 bg-red-200 text-red-900 rounded-full font-bold">
                    🚨 KRIS{lastDecisionDebug.crisis_type ? `: ${lastDecisionDebug.crisis_type}` : ''}
                  </span>
                )}
                {lastDecisionDebug.insight_confirmed && <span className="px-2 py-0.5 bg-green-200 text-green-900 rounded-full">💡 insikt bekräftad</span>}
                {lastDecisionDebug.insight_emerging && !lastDecisionDebug.insight_confirmed && <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full">💡 insikt växer</span>}
                {lastDecisionDebug.resistance && <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full">⚡ motstånd</span>}
                {lastDecisionDebug.heavy_topic && <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">⚠️ tungt ämne</span>}
                {lastDecisionDebug.change_wish && <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full">🌱 förändringslust</span>}
                {!lastDecisionDebug.affect && !lastDecisionDebug.crisis && !lastDecisionDebug.insight_emerging &&
                  !lastDecisionDebug.resistance && !lastDecisionDebug.heavy_topic && !lastDecisionDebug.change_wish && (
                  <span className="text-gray-400 italic">inga aktiva signaler</span>
                )}
              </div>
            </div>

            {/* Row 4: Tool suggestions */}
            {lastDecisionDebug.tool_suggestions?.length > 0 && (
              <div className="border-t border-purple-200 pt-2">
                <span className="text-purple-500 uppercase tracking-wide text-[10px] block mb-1">Tool-förslag ({lastDecisionDebug.tool_suggestions.length})</span>
                <div className="space-y-1">
                  {lastDecisionDebug.tool_suggestions.map((t, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="px-1.5 py-0 bg-indigo-100 text-indigo-700 rounded font-bold whitespace-nowrap">
                        {t.tool}
                      </span>
                      <span className="text-gray-500">{t.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>)}
          </div>
        )}
      </div>

      {/* Stats */}
      {/* {stats && (
        <div className="bg-gray-50 p-3 border-b">
          <div className="text-sm text-gray-600">
            {stats.message ? (
              <span>{stats.message}</span>
            ) : (
              <span>
                Totalt: {stats.total_conversations || 0} samtal
                {stats.high_importance_count > 0 && (
                  <span>, Viktiga: {stats.high_importance_count}</span>
                )}
                {stats.security_level && (
                  <span className="ml-2">
                    | Säkerhet: {stats.security_level}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )} */}

      {/* Error Banner */}
      {error && (
        <div className="bg-red-100 text-red-800 p-3 px-6 flex justify-between items-center border-b border-gray-200">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="bg-transparent border-none text-red-800 text-lg cursor-pointer p-0 w-6 h-6 flex items-center justify-center">×</button>
        </div>
      )}

      {/* Messages Container - Full height wrapper for centering */}
      <div className="flex-1 flex flex-col">
        {/* Empty state with centered content and input */}
        {!loadingHistory && messages.length === 0 && !isTherapistView && (
          <div className={`flex-1 flex flex-col mt-32 items-center px-2 sm:px-6 space-y-8 transition-all duration-300 ${
            showAIReviewPanel ? 'mr-96' : ''
          }`}>
            {/* Welcome Section */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto">
                <Icon name="message-circle-heart" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Vad har du för några känslor idag?
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                Jag är här för att lyssna och stödja dig. <br/>Berätta vad du har på hjärtat.
              </p>
              
              {/* Progressive Anamnesis Gentle Introduction */}
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200 max-w-lg mx-auto">
                <div className="text-sm text-green-800">
                  <div className="font-medium mb-2">Organisk approach</div>
                  <div className="text-xs leading-relaxed">
                    Medan vi chattar kommer jag gradvis lära känna dig bättre. 
                    Inget formulär att fylla i - bara naturliga samtal som bygger förståelse över tid.
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex text-emerald-600">
                <Shield className="w-5 h-5 mr-1" />
                <span className="text-sm">All chat-data är krypterad och kan endast läsas av dig och din terapeut</span>
              </div>
            </div>

            {/* Centered Input */}
            <div className="w-full max-w-2xl">
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 shadow-lg">
                {/* Textarea without border */}
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={loading ? "AI:n tänker..." : "Berätta hur du mår eller fråga om något..."}
                  disabled={loading}
                  maxLength="1000"
                  className="w-full bg-transparent border-0 resize-none focus:outline-none placeholder-gray-500 text-gray-900 text-base leading-relaxed"
                  rows="2"
                  style={{ minHeight: '48px' }}
                />
                
                {/* Second row with info and send button */}
                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{input.length}/1000</span>
                    <div className="flex items-center text-emerald-500 font-medium">
                      <Shield className="w-3 h-3 mr-1 text-emerald-600" />
                      Ultra-säker kryptering
                    </div>
                  </div>
                  
                  <Button 
                    onClick={sendMessage} 
                    disabled={loading || !input.trim()}
                    className="w-10 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Insight-banner – visas när AI:n flaggar en insikt att spara */}
        {pendingInsight && (
          <div className="mx-4 my-2 p-3 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
            <span className="text-lg">💡</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-green-800 mb-0.5">Insikt att spara</p>
              <p className="text-sm text-green-900 break-words">{pendingInsight.text}</p>
              <p className="text-xs text-green-600 mt-0.5 capitalize">{pendingInsight.category}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0 mt-0.5">
              <button
                onClick={async () => {
                  try {
                    await apiService.saveChatInsight(
                      { insight_text: pendingInsight.text, category: pendingInsight.category },
                      token
                    );
                    setPendingInsight(null);
                    console.log('💡 Insikt sparad');
                  } catch (e) {
                    console.error('❌ Kunde inte spara insikt', e);
                  }
                }}
                className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 font-semibold"
              >
                Spara
              </button>
              <button
                onClick={() => setPendingInsight(null)}
                className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200"
              >
                Hoppa
              </button>
            </div>
          </div>
        )}

        {/* Regular Messages Container - only when there are messages */}
        {(loadingHistory || messages.length > 0 || isTherapistView) && (
          <div className={`flex-1 p-4 px-2 sm:px-6 max-w-4xl mx-auto w-full overflow-y-auto transition-all duration-300 ${
            showAIReviewPanel ? 'mr-96' : ''
          }`}>
            {loadingHistory && (
              <div className="text-center py-10 text-gray-600">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                <div className="flex gap-1 justify-center items-center"><Icon name="shield"/><span>Laddar säker chat-historik...</span></div>
              </div>
            )}

            {/* Empty state for therapist view */}
            {!loadingHistory && messages.length === 0 && isTherapistView && (
              <div className="text-center py-10 text-gray-600">
                <p className="my-2">Inga meddelanden att visa.</p>
                <p className="my-2">Klienten har inte chattat med AI:n än.</p>
              </div>
            )}

            {/* Modern instant chat messages */}
            {console.log(`🎯 RENDER: messages.length = ${messages.length}`) || ''}
            {messages.map((msg) => {
              // ── Ny konversation-divider ────────────────────────────────
              if (msg.role === 'divider') {
                return (
                  <div key={msg.id} className="flex items-center gap-3 my-6 px-2">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 whitespace-nowrap select-none flex items-center gap-1.5">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Ny konversation
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                );
              }
              // ── Normalt meddelande ─────────────────────────────────────
              return (
              <div 
                key={msg.id} 
                id={`message-${msg.id}`}
                className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} transition-all duration-300 ${
                  highlightedMessageId === msg.id ? 'bg-yellow-50 border-2 border-yellow-200 rounded-lg p-2' : ''
                }`}
              >
                <div className={`max-w-xs sm:max-w-md lg:max-w-lg xl:max-w-xl px-4 py-3 rounded-2xl shadow-sm ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-md'
                    : msg.role === 'assistant'
                    ? 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                    : 'bg-red-50 text-red-800 border border-red-200'
                } ${msg.isArchived ? 'opacity-60' : ''} ${
                  highlightedMessageId === msg.id ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''
                }`}>
                  
                  {/* Message content */}
                  <div className="space-y-2">
                    {/* Role icon and metadata badges */}
                    <div className="flex items-center justify-between">
                      {/* Role icon */}
                      <div className={`flex items-center gap-1 ${
                        msg.role === 'user' ? 'text-white/90' : 'text-gray-600'
                      }`}>
                        {msg.role === 'user' ? (
                          <User className="w-4 h-4" />
                        ) : msg.role === 'assistant' ? (
                          <Bot className="w-4 h-4" />
                        ) : null}
                      </div>
                      
                      {/* Metadata badges */}
                      {(msg.importance >= 4 || msg.messageType) && (
                        <div className="flex flex-wrap gap-1">
                          {msg.importance >= 4 && (
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                              msg.role === 'user' 
                                ? 'bg-white/20 text-white' 
                                : 'bg-blue-50 text-blue-700'
                            }`}>
                              {msg.importance === 5 ? '🔴' : '🟡'}
                              {' '}
                              {msg.importance}⭐
                            </span>
                          )}
                          {getTypeBadge(msg.messageType, msg.role)}
                        </div>
                      )}
                    </div>
                    
                    {/* Main message text */}
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {msg.messageType === 'therapist_suggestion' ? (
                        <div>
                          {msg.content.split('[Klicka här för mer information]').map((part, index) => (
                            <span key={index}>
                              {part}
                              {index === 0 && (
                                <button
                                  onClick={() => setShowTherapistModal(true)}
                                  className="inline-block bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 border border-white/30 hover:border-white/50 mx-1"
                                >
                                  Klicka här för mer information 💬
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        msg.content
                      )}
                    </div>
                    
                    {/* Timestamp and security/AI review controls */}
                    <div className={`flex items-center justify-between gap-2 text-xs ${
                      msg.role === 'user' ? 'text-white/70' : 'text-gray-500'
                    } mt-1`}>
                      <span>{formatTime(msg.timestamp)}</span>
                      
                      <div className="flex items-center gap-2">
                        {/* AI Review button for therapists on assistant messages */}
                        {isTherapistView && msg.role === 'assistant' && msg.conversationId && (
                          <button
                            onClick={() => handleOpenAIReview(msg.conversationId, msg.id)}
                            className="inline-flex items-center px-2 py-1 text-xs bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-full border border-purple-200 transition-colors"
                            title="Granska AI-respons med kvalitetsanalys (kan vara tillgänglig eller ej beroende på när konversationen skedde)"
                          >
                            <Brain className="w-3 h-3 mr-1" />
                            Granska AI
                          </button>
                        )}
                        
                        {/* Security indicator for encrypted messages */}
                        {!isTherapistView && msg.securityLevel && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full ${
                            msg.role === 'user' 
                              ? 'bg-white/20 text-white/80 border-white/30' 
                              : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}>
                            <Shield className="w-3 h-3 mr-1" />
                            <span className="text-xs">Krypterad</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
            
            {/* Loading indicator */}
            {loading && (
              <div className="mb-4 flex justify-start">
                <div className="max-w-xs bg-white border border-gray-200 rounded-2xl rounded-bl-md p-4 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                    <span className="text-gray-500 text-xs">AI tänker...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Modern Input Area - only show when there are messages and not therapist view */}
        {!isTherapistView && messages.length > 0 && (
          <div className={`sticky bottom-0 ai-bg rounded-2xl transition-all duration-300 ${
            showAIReviewPanel ? 'mr-96' : ''
          }`}>
            <div className="max-w-4xl mx-auto pb-2">
              {/* Känslo-widget – visas när AI:n ber om en 1-10-skattning */}
              {pendingFeelingCheck && !loading && (
                <div className="mb-2 flex justify-start px-2 sm:px-6">
                  <div className="max-w-xs sm:max-w-md bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm px-4 py-3">
                    <div className="flex items-center gap-1 text-gray-600 mb-2">
                      <Bot className="w-4 h-4" />
                    </div>
                    <p className="text-sm text-gray-800 mb-1">Innan vi fortsätter — hur känns det just nu, på en skala?</p>
                    <p className="text-[11px] text-gray-400 mb-3">1 = mår jättebra &nbsp;·&nbsp; 10 = akut kris</p>
                    <div className="flex gap-1 flex-wrap">
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button
                          key={n}
                          onClick={() => sendFeelingScore(n)}
                          className={`w-8 h-8 rounded-lg text-sm font-bold transition-all hover:scale-110 active:scale-95
                            ${n <= 3 ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                              n <= 6 ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' :
                              'bg-red-100 text-red-700 hover:bg-red-200'}`}
                        >{n}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-lg ">
                {/* Textarea without border */}
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={loading ? "AI:n tänker..." : "Skriv ditt meddelande..."}
                  disabled={loading}
                  maxLength="1000"
                  className="w-full bg-transparent border-0 resize-none focus:outline-none placeholder-gray-500 text-gray-900 text-base leading-relaxed"
                  rows="1"
                  style={{ minHeight: '24px' }}
                />
                
                {/* Second row with info and send button */}
                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {/* Show therapist suggestion icon for users with >5 messages */}
                    {messages.length > 5 ? (
                      <button
                        onClick={() => setShowTherapistModal(true)}
                        className="flex items-center gap-1 text-red-600 hover:text-red-700 transition-colors group"
                        title="Kontakta en mänsklig terapeut"
                      >
                        <MessageCircleHeart className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        <span className="font-medium">Boka samtal</span>
                      </button>
                    ) : (
                      <span>{input.length}/1000</span>
                    )}
                    
                    <div className="flex items-center text-emerald-600">
                      <Shield className="w-3 h-3 mr-1" />
                      <span>Säker kryptering</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={sendMessage} 
                    disabled={loading || !input.trim()}
                    
                  >
                    {loading ? (
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Send className="w-4 h-4" />
                    )} <span className="ml-1">Skicka</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {isTherapistView && (
        <div className="p-4 px-6 border-t border-gray-200 text-center">
          <p className="my-1 text-sm text-gray-600">
            Som terapeut kan du läsa {selectedPatient ? `${selectedPatient.first_name || selectedPatient.name}s` : 'klientens'} AI-konversationer men inte delta i chatten.
          </p>

        <div className="flex gap-1 text-emerald-600 text-sm justify-center"><Shield className="w-5 h-5 mr-1" /> All chat-data är krypterad och kan endast läsas av dig och din klient</div>
        </div>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="fixed bottom-4 right-4 space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded"
            >
              {notification.message}
            </div>
          ))}
        </div>
      )}

      {/* Credits Warning Alert */}
      {!isTherapistView && credits !== null && credits <= 0 && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-yellow-50 border-2 border-yellow-200 text-yellow-800 px-6 py-4 rounded-xl shadow-lg max-w-md mx-auto">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div className="flex-1">
                <p className="font-semibold text-sm">Inga credits kvar!</p>
                <p className="text-xs text-yellow-700">Köp fler credits för att fortsätta chatta</p>
              </div>
              <button 
                onClick={() => setShowPaywall(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors"
              >
                Köp Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      <PaywallModal 
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        credits={credits}
      />

      {/* Therapist Action Modal */}
      <TherapistActionModal 
        isOpen={showTherapistModal}
        onClose={() => setShowTherapistModal(false)}
      />

      {/* AI Review Panel for therapists - v2.0 with messageId */}
      {isTherapistView && (
        <AIReviewPanel
          isOpen={showAIReviewPanel}
          onClose={closeAIReview}
          messageId={highlightedMessageId}  // v2.0: Use highlighted message as primary ID
          token={token}
          patientName={selectedPatient?.name}
          highlightedMessageId={highlightedMessageId}
          onHighlightMessage={handleHighlightMessage}
          chatMessages={messages}
        />
      )}
    </div>
  );
});

export default ChatWithSecurity;
